import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Bell,
  Check,
  CheckCheck,
  Trash2,
  Trophy,
  UserPlus,
  FileText,
  ThumbsUp,
  RefreshCw,
  Gift,
  Ticket,
  Info,
} from "lucide-react";
import { useState } from "react";
import { useLocation } from "wouter";
import { formatDistanceToNow } from "date-fns";

const typeIcons: Record<string, typeof Bell> = {
  player_joined: UserPlus,
  submission_added: FileText,
  vote_received: ThumbsUp,
  game_status_changed: RefreshCw,
  settlement_completed: Trophy,
  referral_earned: Gift,
  coupon_received: Ticket,
  system: Info,
};

const typeColors: Record<string, string> = {
  player_joined: "text-neon",
  submission_added: "text-blue-400",
  vote_received: "text-amber-400",
  game_status_changed: "text-purple-400",
  settlement_completed: "text-emerald-400",
  referral_earned: "text-pink-400",
  coupon_received: "text-orange-400",
  system: "text-slate-400",
};

export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [, setLocation] = useLocation();
  const utils = trpc.useUtils();

  const { data: unreadCount = 0 } = trpc.notification.unreadCount.useQuery(undefined, {
    refetchInterval: 15_000, // Poll every 15 seconds
  });

  const { data: notifications = [], isLoading } = trpc.notification.list.useQuery(
    { limit: 10, offset: 0 },
    { enabled: open } // Only fetch when dropdown is open
  );

  const markRead = trpc.notification.markRead.useMutation({
    onSuccess: () => {
      utils.notification.unreadCount.invalidate();
      utils.notification.list.invalidate();
    },
  });

  const markAllRead = trpc.notification.markAllRead.useMutation({
    onSuccess: () => {
      utils.notification.unreadCount.invalidate();
      utils.notification.list.invalidate();
    },
  });

  const clearAll = trpc.notification.clear.useMutation({
    onSuccess: () => {
      utils.notification.unreadCount.invalidate();
      utils.notification.list.invalidate();
    },
  });

  const handleNotificationClick = async (n: (typeof notifications)[0]) => {
    if (!n.isRead) {
      markRead.mutate({ notificationId: n.id });
    }
    if (n.gameId) {
      setOpen(false);
      try {
        const { slug } = await utils.game.getSlugById.fetch({ gameId: n.gameId });
        if (slug) setLocation(`/game/${slug}`);
      } catch {
        // Slug lookup failed — stay on the current page
      }
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button className="relative p-2 rounded-lg hover:bg-white/5 transition-colors text-slate-400 hover:text-white">
          <Bell className="w-[18px] h-[18px]" />
          {unreadCount > 0 && (
            <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center px-1 animate-pulse">
              {unreadCount > 99 ? "99+" : unreadCount}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-[380px] p-0 bg-[#141828] border-white/10 shadow-2xl"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-white/5">
          <h3 className="text-sm font-semibold text-white">Notifications</h3>
          <div className="flex items-center gap-1">
            {unreadCount > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => markAllRead.mutate()}
                className="h-7 px-2 text-xs text-slate-400 hover:text-white hover:bg-white/5"
              >
                <CheckCheck className="w-3.5 h-3.5 mr-1" />
                Read all
              </Button>
            )}
            {notifications.length > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => clearAll.mutate()}
                className="h-7 px-2 text-xs text-slate-400 hover:text-red-400 hover:bg-red-500/10"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            )}
          </div>
        </div>

        {/* Notification list */}
        <div className="max-h-[400px] overflow-y-auto">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="w-5 h-5 border-2 border-neon/30 border-t-teal rounded-full animate-spin" />
            </div>
          ) : notifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-10 px-4">
              <Bell className="w-8 h-8 text-slate-600 mb-2" />
              <p className="text-sm text-slate-500">No notifications yet</p>
              <p className="text-xs text-slate-600 mt-1">
                You'll be notified when players join your games or vote on entries.
              </p>
            </div>
          ) : (
            notifications.map((n) => {
              const Icon = typeIcons[n.type] || Bell;
              const color = typeColors[n.type] || "text-slate-400";
              return (
                <button
                  key={n.id}
                  onClick={() => handleNotificationClick(n)}
                  className={`w-full flex items-start gap-3 px-4 py-3 text-left transition-colors hover:bg-white/5 border-b border-white/[0.03] last:border-0
                    ${!n.isRead ? "bg-neon/[0.03]" : ""}
                  `}
                >
                  <div className={`mt-0.5 p-1.5 rounded-lg ${!n.isRead ? "bg-neon/10" : "bg-white/5"}`}>
                    <Icon className={`w-4 h-4 ${!n.isRead ? color : "text-slate-500"}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className={`text-xs font-medium truncate ${!n.isRead ? "text-white" : "text-slate-400"}`}>
                        {n.title}
                      </p>
                      {!n.isRead && (
                        <span className="w-1.5 h-1.5 rounded-full bg-neon shrink-0" />
                      )}
                    </div>
                    {n.message && (
                      <p className="text-[11px] text-slate-500 mt-0.5 line-clamp-2">{n.message}</p>
                    )}
                    <p className="text-[10px] text-slate-600 mt-1">
                      {n.createdAt ? formatDistanceToNow(new Date(n.createdAt), { addSuffix: true }) : ""}
                    </p>
                  </div>
                  {!n.isRead && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        markRead.mutate({ notificationId: n.id });
                      }}
                      className="mt-1 p-1 rounded hover:bg-white/10 text-slate-500 hover:text-neon transition-colors"
                      title="Mark as read"
                    >
                      <Check className="w-3.5 h-3.5" />
                    </button>
                  )}
                </button>
              );
            })
          )}
        </div>

        {/* Footer */}
        {notifications.length > 0 && (
          <div className="border-t border-white/5 px-4 py-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setOpen(false);
                setLocation("/console/notifications");
              }}
              className="w-full h-8 text-xs text-neon hover:text-neon hover:bg-neon/5"
            >
              View all notifications
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
